import React from "react";
import { format, differenceInDays } from "date-fns";

export default function ParcelCard({ data }) {
  const formatDate = (dateString) => {
    return format(new Date(dateString), "MMM dd, yyyy hh:mm a");
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "claimed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Calculate days unclaimed for parcels that are not in "Claimed" status
  const getDaysUnclaimed = () => {
    if (data.status.toLowerCase() === "claimed") return null;

    const creationDate = new Date(data.createdAt);
    const today = new Date();
    return differenceInDays(today, creationDate);
  };

  // Get color based on number of days unclaimed
  const getDaysUnclaimedColor = (days) => {
    if (days <= 3) return "text-green-600";
    if (days <= 7) return "text-yellow-600";
    return "text-red-600 font-bold";
  };

  const daysUnclaimed = getDaysUnclaimed();

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4 hover:shadow-lg transition-shadow duration-200">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Image Section */}
        <div className="w-full md:w-48 h-48">
          <img
            src={data.imageUrl}
            alt={`Parcel ${data.parcelNumber}`}
            className="w-full h-full object-cover rounded-lg"
          />
        </div>

        {/* Content Section */}
        <div className="flex-1">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Parcel #{data.parcelNumber}
              </h3>
              <p className="text-sm text-gray-600">{data.unitNumber}</p>
            </div>
            <div className="flex flex-col items-end">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  data.status
                )}`}
              >
                {data.status}
              </span>
              {daysUnclaimed !== null && (
                <span
                  className={`mt-1 text-sm ${getDaysUnclaimedColor(
                    daysUnclaimed
                  )}`}
                >
                  {daysUnclaimed} {daysUnclaimed === 1 ? "day" : "days"}{" "}
                  unclaimed
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-gray-600">Recipient</p>
              <p className="font-medium">{data.recipientName}</p>
            </div>
            <div>
              <p className="text-gray-600">Delivery Company</p>
              <p className="font-medium">{data.deliveryCompany}</p>
            </div>
            <div>
              <p className="text-gray-600">Storage Location</p>
              <p className="font-medium">{data.storageLocation}</p>
            </div>
            <div>
              <p className="text-gray-600">Created By</p>
              <p className="font-medium">{data.createdBy}</p>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            <p>Created: {formatDate(data.createdAt)}</p>
            {data.collectedAt && (
              <p>Collected: {formatDate(data.collectedAt)}</p>
            )}
          </div>

          {data.status === "Claimed" && (
            <div className="mt-4 text-sm text-gray-600">
              <p>Released By: {data.releasedByName}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
